import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import { jobsApi, isJobContentLocked, type JobDetailDto } from "@/lib/jobs";
import { usePermissions } from "@/hooks/use-permissions";
import { MfgBuilderPanel } from "@/components/mfg/builder/MfgBuilderPanel";

export interface JobOrderDetsTabProps {
  jobId: number;
  /** When provided, mfg (tree-BOM) lines gain an "Edit build" action gated on
   *  mfg-bom:write + the job not being content-locked. Absent in tests. */
  job?: JobDetailDto;
}

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function JobOrderDetsTab({ jobId, job }: JobOrderDetsTabProps) {
  const { has } = usePermissions();
  const [editMfgBomId, setEditMfgBomId] = useState<number | null>(null);
  const canEditMfg = !!job && has("mfg-bom:write") && !isJobContentLocked(job.currentStatusCode);

  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["jobs", jobId, "items"],
    queryFn: () => jobsApi.items(jobId),
  });

  if (isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading items…</p>;
  if (isError)   return <p role="alert" className="p-4 text-sm text-destructive">Failed to load items.</p>;
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No items on this job.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-[#0f3453] text-white">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-4 py-2">#</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2 text-right">Qty</th>
              <th className="px-4 py-2 text-right">Unit price ex-GST</th>
              <th className="px-4 py-2 text-right">Line total ex-GST</th>
              {canEditMfg && <th className="px-4 py-2 text-right" aria-label="Actions"></th>}
            </tr>
          </thead>
          <tbody>
            {data.map((it) => (
              <tr key={it.jobItemId} className="border-b">
                <td className="px-4 py-2 text-muted-foreground">{it.sortOrder}</td>
                <td className="px-4 py-2">{it.description}</td>
                <td className="px-4 py-2 text-right">{it.quantity}</td>
                <td className="px-4 py-2 text-right">{fmt.format(it.unitPriceExGst)}</td>
                <td className="px-4 py-2 text-right">{fmt.format(it.lineTotalExGst)}</td>
                {canEditMfg && (
                  <td className="px-4 py-2 text-right">
                    {it.mfgBomId != null && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                        aria-label={`Edit build ${it.description}`}
                        onClick={() => setEditMfgBomId(it.mfgBomId)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      {/* Task 13: edit an existing manufacturing line — reopens the builder in edit mode. */}
      <Dialog open={editMfgBomId != null} onOpenChange={(o) => { if (!o) setEditMfgBomId(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit manufacturing item</DialogTitle>
            <DialogDescription className="sr-only">Reconfigure a tree BOM and reprice it.</DialogDescription>
          </DialogHeader>
          {editMfgBomId != null && (
            <MfgBuilderPanel
              key={editMfgBomId}
              context={{ kind: "job", id: jobId }}
              mfgBomId={editMfgBomId}
              onCancel={() => setEditMfgBomId(null)}
              onAdded={() => setEditMfgBomId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
